/**
 * `content.events` — data schemas (events / announcements). A curated, ordered
 * list of dated happenings (special dinners, tastings, workshops, openings) —
 * a feature Veldon proposes to every client. Kept deliberately small and
 * general (not venue-specific): a title, a real calendar date, an optional
 * time, description, image, and an optional call-to-action link.
 *
 * Rendering stays PURE and deterministic (no "now"): events render in the
 * owner's curated order, exactly like menu items and products — so the same
 * snapshot always renders identically (content-addressing holds). "Upcoming
 * only" filtering would need a clock and is therefore intentionally absent.
 */
import { z } from 'zod';
import { ImageValue, Slug } from '../../schema/types.js';

/** Max events in one instance. An events list is a curated handful, not a feed;
 *  bounded well below anything that could bloat a snapshot. */
export const MAX_EVENTS = 100;

/** A calendar date, YYYY-MM-DD, that is a REAL day (rejects 2026-13-40 and
 *  2026-02-30). Stored as a plain ISO date string — an event happens on a day,
 *  with no time zone. */
export const EventDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be in YYYY-MM-DD form')
  .refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const dt = new Date(Date.UTC(year!, month! - 1, day!));
    return dt.getUTCFullYear() === year && dt.getUTCMonth() === month! - 1 && dt.getUTCDate() === day;
  }, 'date must be a real calendar day');

/** Optional start time, HH:MM, 24-hour. */
export const EventTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'time must be HH:MM (24-hour)');

export const EventItem = z
  .object({
    id: Slug,
    title: z.string().min(1).max(160),
    date: EventDate,
    time: EventTime.optional(),
    description: z.string().max(600).optional(),
    image: ImageValue.optional(),
    /** A call-to-action (tickets / more info). Label and URL travel together. */
    ctaLabel: z.string().min(1).max(60).optional(),
    ctaUrl: z.string().max(2000).optional(),
  })
  .strict()
  .superRefine((event, ctx) => {
    // A lone label or a lone URL is a half-configured button the render can't
    // honor — require both or neither. (The URL's scheme is enforced at render
    // via safeUrl, the same canonical policy the freeform sink uses.)
    if ((event.ctaLabel === undefined) !== (event.ctaUrl === undefined)) {
      ctx.addIssue('ctaLabel and ctaUrl must be provided together');
    }
  });

export const EventsData = z
  .object({
    events: z.array(EventItem).max(MAX_EVENTS),
  })
  .strict()
  .refine(
    // Event ids are the addressing key operations (update/remove/reorder) use,
    // so they MUST be unique — a duplicate would make update/remove act on the
    // first match and silently diverge from what is displayed.
    (data) => new Set(data.events.map((event) => event.id)).size === data.events.length,
    'event ids must be unique within the module',
  );

export const EventsConfig = z.object({
  /** Locale for date formatting in the rendered view (mirrors the other
   *  modules). Defaults to Czech — the primary cs/pl market. Layout (list vs
   *  cards) is owned by the mount's `view`, not config, so there is no `view`
   *  knob here — the two registered views ARE the layout selector. */
  locale: z.enum(['cs', 'en', 'pl']).default('cs'),
  /** Optional heading rendered above the events. */
  heading: z.string().max(120).optional(),
});

export type EventItemT = z.infer<typeof EventItem>;
export type EventsDataT = z.infer<typeof EventsData>;
export type EventsConfigT = z.infer<typeof EventsConfig>;
